import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { SEO, StructuredData } from '@/components/seo'

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-surface-950 text-white">
      <SEO
        title="Privacy Policy"
        description="JumpstoneHR Privacy Policy. Learn how we collect, use, and protect your personal information. PIPEDA and GDPR compliant. Your data is never used to train our AI models."
        canonical="/privacy"
        structuredData={[
          StructuredData.webPage(
            'Privacy Policy',
            'JumpstoneHR privacy policy explaining data collection, use, and protection practices.',
            '/privacy'
          ),
          StructuredData.breadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Privacy Policy', url: '/privacy' },
          ]),
        ]}
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          <div className="relative mx-auto max-w-4xl section-padding">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 max-w-[60px] bg-brand-500" />
              <span className="text-brand-400 text-sm font-medium tracking-widest uppercase">Legal</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Privacy Policy
            </h1>
            
            <p className="text-surface-400">
              Last updated: January 1, 2026
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="pb-24">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="prose prose-invert prose-lg max-w-none">
              <div className="space-y-12">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    Jumpstone Technology Inc. ("Jumpstone", "we", "us", or "our") is an Ontario corporation registered in Canada. We are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner.
                  </p>
                  <p className="text-surface-300 leading-relaxed">
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use JumpstoneHR, our AI-powered hiring decision platform. This policy complies with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Information We Collect</h2>
                  
                  <h3 className="text-xl font-semibold text-white mb-3 mt-6">Information You Provide</h3>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Account registration information (name, email address, company name)</li>
                    <li>Billing and payment information</li>
                    <li>Candidate data uploaded to the platform (resumes, interview notes, assessments)</li>
                    <li>Job descriptions and hiring criteria</li>
                    <li>Communications with our support team</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-white mb-3 mt-6">Information Collected Automatically</h3>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Device and browser information</li>
                    <li>IP address and approximate location</li>
                    <li>Usage data and interaction with our platform</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">How We Use Your Information</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">We use the information we collect to:</p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Provide and maintain our hiring decision platform</li>
                    <li>Process and analyze candidate data using AI to generate hiring insights</li>
                    <li>Improve and personalize your experience</li>
                    <li>Process payments and manage your subscription</li>
                    <li>Send important notifications about your account or the service</li>
                    <li>Respond to your inquiries and provide customer support</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">AI Processing and Candidate Data</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    Our platform uses artificial intelligence to analyze candidate information and provide hiring recommendations. It's important to understand:
                  </p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>AI-generated insights are recommendations only; final hiring decisions remain with humans</li>
                    <li>We do not use candidate data to train our AI models without explicit consent</li>
                    <li>All AI processing is auditable and decisions can be explained</li>
                    <li>We implement bias detection and mitigation measures in our AI systems</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Data Sharing and Disclosure</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">We do not sell your personal information. We may share information with:</p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Service providers who assist in operating our platform (hosting, payment processing)</li>
                    <li>Professional advisors (lawyers, accountants) when necessary</li>
                    <li>Law enforcement or regulatory bodies when required by law</li>
                    <li>Third parties in connection with a merger, acquisition, or sale of assets</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Data Retention</h2>
                  <p className="text-surface-300 leading-relaxed">
                    We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements. When you close your account, we will delete or anonymize your data within 90 days, unless we are required to retain it for legal purposes.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Your Rights</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">Under PIPEDA and applicable privacy laws, you have the right to:</p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Access your personal information</li>
                    <li>Correct inaccurate or incomplete information</li>
                    <li>Request deletion of your information</li>
                    <li>Withdraw consent for data processing</li>
                    <li>Lodge a complaint with the Office of the Privacy Commissioner of Canada</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Data Security</h2>
                  <p className="text-surface-300 leading-relaxed">
                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption in transit and at rest, access controls, and regular security assessments.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">International Data Transfers</h2>
                  <p className="text-surface-300 leading-relaxed">
                    Your information may be stored and processed in Canada and other countries where our service providers are located. When we transfer data internationally, we ensure appropriate safeguards are in place to protect your information in accordance with this policy.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    If you have questions about this Privacy Policy or wish to exercise your rights, please contact our Privacy Officer:
                  </p>
                  <div className="bg-surface-900 border border-surface-800 rounded-xl p-6">
                    <p className="text-white font-semibold">Jumpstone Technology Inc.</p>
                    <p className="text-surface-400 mt-2">Privacy Officer</p>
                    <p className="text-surface-400">Email: privacy@jumpstonehr.com</p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Changes to This Policy</h2>
                  <p className="text-surface-300 leading-relaxed">
                    We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  )
}

